import React, { useState, useEffect } from "react";
import { Navigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { 
    fetchUserJoinedSquads, 
    findSquadByCode, 
    joinSquad,
    fetchTournaments 
} from "../api/api";
import JoinedSquadCard from "../components/JoinedSquadCard";
import AdminSquadCard from "../components/AdminSquadCard";
import CreateSquadForm from "../components/CreateSquadForm";
import {
    getTournamentData,
    getTournamentDisplayName,
    buildTournamentMap
} from "../utils/tournamentUtils";

const Squads = () => {
    const { user, loadingUser } = useAuth();

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [mode, setMode] = useState("join-squads"); // "join-squads", "admin-squads"
    const [joinedSquads, setJoinedSquads] = useState(null);
    const [joinCode, setJoinCode] = useState("");
    const [findLoading, setFindLoading] = useState(false);
    const [findError, setFindError] = useState(null);
    const [foundSquad, setFoundSquad] = useState(null);
    const [selectedArenaIds, setSelectedArenaIds] = useState([]);
    const [joiningSquad, setJoiningSquad] = useState(false);
    const [tournaments, setTournaments] = useState([]);

    const tournamentMap = buildTournamentMap(tournaments);

    useEffect(() => {
        if (!user || loadingUser) {
            return;
        }

        loadJoinedSquads();
    }, [user, loadingUser]);

    const loadJoinedSquads = async () => {
        setLoading(true);
        setError(null);

        try {
            const response = await fetchUserJoinedSquads();
            setJoinedSquads(response.data);
        } catch (err) {
            setError(err.message);
            if (err.message === 'Forbidden') {
                window.location.href = "/predict";
            }
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        const initialize = async () => {
            const { tournaments } =
                await getTournamentData(
                    fetchTournaments
                );

            setTournaments(tournaments);
        };

        initialize();
    }, []);

    const renderTournamentName = (id) => {
        const tournament = tournamentMap[id];

        if (!tournament) {
            return "Unknown tournament";
        }

        return `${getTournamentDisplayName(
            tournament.name
        )} ${tournament.season}`;
    };

    const adminSquads = joinedSquads?.filter(
        (squad) => squad.userRole === "admin"
    ) || [];

    const handleFindSquad = async () => {
        if (!joinCode.trim()) {
            return;
        }

        const alreadyJoined = joinedSquads?.some(
            (squad) => squad.code === joinCode.trim()
        );

        if (alreadyJoined) {
            setFindError("Already a member");
            setFoundSquad(null);
            return;
        }

        try {
            setFindLoading(true);
            setFindError(null);

            const response = await findSquadByCode(joinCode);

            const squad = response.data;

            setFoundSquad(squad);

            // auto include ONLY if exactly one arena exists
            if (squad.arenas.length === 1) {
                setSelectedArenaIds([squad.arenas[0].id]);
            } else {
                setSelectedArenaIds([]);
            }

        } catch (err) {
            setFindError(err.message);
            setFoundSquad(null);
        } finally {
            setFindLoading(false);
        }
    };

    const toggleArenaSelection = (arenaId) => {

        if (selectedArenaIds.includes(arenaId)) {
            setSelectedArenaIds(
                selectedArenaIds.filter((id) => id !== arenaId)
            );
        } else {
            setSelectedArenaIds([
                ...selectedArenaIds,
                arenaId
            ]);
        }
    };

    const handleJoinSquad = async () => {

        if (!foundSquad) {
            return;
        }

        try {
            setJoiningSquad(true);

            await joinSquad(
                foundSquad.slug,
                joinCode,
                selectedArenaIds
            );

            await loadJoinedSquads();

            setJoinCode("");
            setFoundSquad(null);
            setSelectedArenaIds([]);

        } catch (err) {
            alert(err.message);
        } finally {
            setJoiningSquad(false);
        }
    };

    const renderJoinSquads = () => {
        return (
            <div className="squads-section">

                <h2 className="squads-title">
                    Joined Squads
                </h2>

                {!joinedSquads || joinedSquads.length === 0 ? (
                    <p>No joined squads.</p>
                ) : (
                    <div className="squads-list">
                        {joinedSquads.map((squad) => (
                            <JoinedSquadCard
                                key={squad.slug}
                                squad={squad}
                            />
                        ))}
                    </div>
                )}
            </div>
        );
    };

    const renderJoinNewSquad = () => {
        return (
            <div className="join-new-squad-section">

                <h2 className="squads-title">
                    Join New Squad
                </h2>

                <div className="join-squad-find-row">

                    <input
                        type="text"
                        className="join-squad-input"
                        placeholder="Enter squad code"
                        value={joinCode}
                        onChange={(e) => {
                            setJoinCode(e.target.value);
                            setFindError(null);
                        }}
                    />

                    <button
                        className="submit-button"
                        onClick={handleFindSquad}
                        disabled={findLoading}
                    >
                        {findLoading ? "Finding..." : "Find"}
                    </button>
                </div>

                {findError && (
                    <p className="alert-text">
                        {findError}
                    </p>
                )}

                {foundSquad && (
                    <div className="join-preview-card">

                        <div className="squad-header">

                            <div className="squad-card-left">
                                <img
                                    src={foundSquad.countryFlag}
                                    alt={foundSquad.country}
                                    title={foundSquad.country}
                                    className="squad-country-flag"
                                />
                            </div>

                            <div className="squad-card-right">

                                <div className="squad-name">
                                    {foundSquad.name}
                                </div>

                                <div className="squad-admin">
                                    admin: {foundSquad.adminUsername}
                                </div>
                            </div>
                        </div>

                        <div className="join-preview-arenas">

                            {foundSquad.arenas.map((arena) => (
                                <div
                                    key={arena.id}
                                    className="arena-card"
                                >

                                    <div className="arena-title">
                                        Arena #{arena.id}
                                    </div>

                                    <div className="arena-tournaments">

                                        {arena.enabledTournaments.map((tournament) => (
                                            <div
                                                key={tournament.id}
                                                className="arena-tournament-row"
                                            >
                                                <span className="arena-tournament-name">
                                                    {renderTournamentName(tournament.id)}
                                                </span>

                                                <span className="arena-fixture">
                                                    from fixture {tournament.fromFixture}
                                                </span>
                                            </div>
                                        ))}
                                    </div>

                                    <div className="arena-users-count">
                                        👥 {arena.joinedUsersCount} joined users
                                    </div>

                                    <button
                                        className={
                                            selectedArenaIds.includes(arena.id)
                                                ? "arena-toggle-button active"
                                                : "arena-toggle-button"
                                        }
                                        onClick={() =>
                                            toggleArenaSelection(arena.id)
                                        }
                                    >
                                        {selectedArenaIds.includes(arena.id)
                                            ? "INCLUDED"
                                            : "JOIN"}
                                    </button>
                                </div>
                            ))}
                        </div>

                        <div className="join-squad-action">

                            <button
                                className="submit-button"
                                onClick={handleJoinSquad}
                                disabled={joiningSquad}
                            >
                                {joiningSquad
                                    ? "Joining..."
                                    : "Join Squad"}
                            </button>
                        </div>
                    </div>
                )}
            </div>
        );
    };

    const renderAdminSquads = () => {
        const adminSquads = joinedSquads?.filter(
            (squad) => squad.userRole === "admin"
        ) || [];

        return (
            <div className="admin-squads-section">

                <h2 className="squads-title">
                    My Squads
                </h2>

                {adminSquads.length === 0 ? (
                    <p>No created squads.</p>
                ) : (
                    <div className="squads-list">

                        {adminSquads.map((squad) => (
                            <AdminSquadCard
                                key={squad.slug}
                                squad={squad}
                                onArenaCreated={loadJoinedSquads}
                            />
                        ))}
                    </div>
                )}

                <CreateSquadForm
                    squadsCount={adminSquads.length}
                    onCreated={loadJoinedSquads}
                />
            </div>
        );
    };

    if (loadingUser) return <p>Loading...</p>;
    if (!user) return <Navigate to="/predict" replace />;
    if (loading) return <p>Loading...</p>;
    if (error) return <p>Error: {error}</p>;

    return (
        <div className="content-container">

            <div className="filter-dropdown-container">
                <select
                    className="filter-dropdown points-dropdown"
                    value={mode}
                    onChange={(e) => setMode(e.target.value)}
                >
                    <option value="join-squads">Join squads</option>
                    <option value="admin-squads">Admin squads</option>
                </select>
            </div>

            <div className="user-points dark-main-container scroll-wrapper">
                {mode === "join-squads" && (
                    <>
                        {renderJoinSquads()}
                        {renderJoinNewSquad()}
                    </>
                )}
                {mode === "admin-squads" && renderAdminSquads()}
            </div>

        </div>
    )
}

export default Squads;